import { useState, useEffect } from 'react';

interface InterestModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectName?: string;
}

export default function InterestModal({ isOpen, onClose, projectName = "مشروع مساحة شباب" }: InterestModalProps) {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    interestType: '',
    preferredDateTime: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://formspree.io/f/mwvvvpkk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          project: projectName,
          fullName: formData.fullName,
          phone: formData.phone,
          interestType: formData.interestType,
          preferredDateTime: formData.preferredDateTime,
          message: formData.message,
          _subject: `شراكة جديدة: ${formData.fullName} - ${projectName}`,
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ fullName: '', phone: '', interestType: '', preferredDateTime: '', message: '' });
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        role="dialog"
        className="relative w-full max-w-lg bg-white rounded-lg shadow-2xl overflow-hidden"
        style={{ maxHeight: 'calc(100vh - 2rem)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="overflow-y-auto p-6" style={{ maxHeight: 'calc(100vh - 2rem)' }}>
        {/* Header */}
        <div className="flex flex-col space-y-1.5 text-right">
          <h2 className="tracking-tight text-2xl font-bold text-right text-gray-900">
            سجل اهتمامك
          </h2>
          <p className="text-sm text-gray-600 text-right">
            <span className="font-semibold text-[#FF6B6B]">المشروع: {projectName}</span>
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Name & Phone */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                الاسم الكامل
              </label>
              <input
                type="text"
                className="flex h-10 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FF6B6B] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 w-full text-right"
                placeholder="أدخل اسمك الكامل"
                required
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                رقم التواصل
              </label>
              <input
                type="tel"
                className="flex h-10 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FF6B6B] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 w-full text-right"
                placeholder="05xxxxxxxx"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
          </div>

          {/* Interest Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
              نوع الاهتمام
            </label>
            <div className="relative">
              <select
                className="flex h-10 items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 ring-offset-background focus:outline-none focus:ring-2 focus:ring-[#FF6B6B] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 w-full text-right appearance-none cursor-pointer"
                required
                value={formData.interestType}
                onChange={(e) => setFormData({ ...formData, interestType: e.target.value })}
                style={{ 
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'left 0.5rem center',
                  backgroundSize: '1.25rem',
                  paddingLeft: '2.5rem'
                }}
              >
                <option value="" disabled className="text-gray-400">اختر نوع الاهتمام</option>
                <option value="strategic-partner" className="text-gray-900">شريك استراتيجي</option>
                <option value="meeting" className="text-gray-900">جدولة اجتماع استكشافي</option>
                <option value="sponsorship" className="text-gray-900">رعاية وتمويل</option>
                <option value="inquiry" className="text-gray-900">استفسار عام</option>
              </select>
            </div>
          </div>

          {/* Preferred Date/Time - Only show when meeting is selected */}
          {formData.interestType === 'meeting' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                الوقت والتاريخ المفضل
              </label>
              <input
                type="text"
                className="flex h-10 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FF6B6B] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 w-full text-right"
                placeholder="مثال: الأحد 15 يناير - الساعة 10 صباحاً"
                value={formData.preferredDateTime}
                onChange={(e) => setFormData({ ...formData, preferredDateTime: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1 text-right">
                اقترح وقتاً وتاريخاً مناسباً لك، وسنتواصل معك لتأكيد الموعد
              </p>
            </div>
          )}

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
              رسالة إضافية (اختيارية)
            </label>
            <textarea
              className="flex min-h-[80px] rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FF6B6B] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 w-full text-right"
              placeholder="أضف أي تفاصيل إضافية..."
              rows={3}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF6B6B] focus:ring-offset-2 h-10 px-4 flex-1 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] hover:opacity-90 text-white py-2 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  جاري الإرسال...
                </>
              ) : submitStatus === 'success' ? (
                <>
                  <i className="ri-check-line"></i>
                  تم الإرسال بنجاح!
                </>
              ) : (
                'إرسال طلب الاهتمام'
              )}
            </button>
            <button
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 h-10 px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
            >
              إلغاء
            </button>
          </div>

          {/* Status Messages */}
          {submitStatus === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-right">
              <p className="text-sm text-red-800 flex items-center gap-2 justify-end">
                <span>حدث خطأ في الإرسال. يرجى المحاولة مرة أخرى.</span>
                <i className="ri-error-warning-line text-red-600"></i>
              </p>
            </div>
          )}
        </form>

        {/* Close Button */}
        <button
          type="button"
          className="absolute left-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-[#FF6B6B] focus:ring-offset-2"
          onClick={onClose}
        >
          <i className="ri-close-line text-xl text-gray-600"></i>
          <span className="sr-only">إغلاق</span>
        </button>
        </div>
      </div>
    </div>
  );
}
